<template>
    <div>

        <!-- Modale : Ajout appareil -->
        <modal name="modal-add-appareil-intervention" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Ajouter un appareil</h2>
                    <button @click="$modal.hide('modal-add-appareil-intervention')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>
                <appareil-form v-model="newAppareil" :simple="true" />
                <div class="flex justify-center mt-6">
                    <button
                        :disabled="loadingCreateAppareil"
                        @click="saveAppareil"
                        class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
                    >
                        <i v-if="loadingCreateAppareil" class="fas fa-spinner fa-spin"></i>
                        <template v-else><i class="fas fa-check"></i> Enregistrer</template>
                    </button>
                </div>
            </div>
        </modal>
        <div class="space-y-6">
            <!-- APPAREIL -->
            <div v-if="client_id">
                <label class="text-sm font-bold text-gray-900">Appareil</label>

                <!-- Chargement -->
                <div v-if="loadingAppareil" class="py-2">
                    <i class="fas fa-spinner fa-spin"></i>
                </div>

                <template v-else>
                    <!-- Aucun appareil → formulaire inline -->
                    <div v-if="appareils.length === 0" class="bg-slate-100 p-4 border rounded mt-1">
                        <appareil-form v-model="newAppareil" :simple="true" />
                        <div class="flex justify-center mt-6">
                            <button
                                :disabled="loadingAddAppareil"
                                @click="saveAppareil"
                                class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
                            >
                                <i v-if="loadingAddAppareil" class="fas fa-spinner fa-spin"></i>
                                <template v-else><i class="fas fa-check"></i> Enregistrer l'appareil</template>
                            </button>
                        </div>
                    </div>

                    <!-- Appareils existants → select + bouton ajout -->
                    <div v-else class="flex gap-2 mt-1">
                        <div class="relative mt-1 grow">
                            <i class="fas fa-fire-flame-simple absolute left-3 top-3 text-gray-400"></i>
                            <select
                                v-model="dataModel.client_appareil_id"
                                @change="$forceUpdate(); loadTypesIntervention()"
                                class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2"
                            >
                                <option value="">Sélectionner un appareil</option>
                                <option v-for="appareil in appareils" :key="appareil.id" :value="appareil.id">
                                    [Ref: {{ appareil.id }}]
                                    {{ appareil.app_type ? appareil.app_type.label : appareil.type }}
                                    <span v-if="appareil.marque"> > {{ appareil.marque }}</span>
                                    <span v-if="appareil.model"> > {{ appareil.model }}</span>
                                </option>
                            </select>
                        </div>
                        <button
                            @click="$modal.show('modal-add-appareil-intervention')"
                            class="bg-slate-200 hover:bg-slate-300 px-3 py-1 rounded-lg flex items-center gap-2 text-xs"
                        >
                            <i class="fas fa-plus"></i>
                        </button>
                    </div>
                </template>
            </div>

            <!-- ─── TYPE D'INTERVENTION ───────────────────────────────────────────── -->
            <div v-if="dataModel.client_appareil_id">
                <label class="text-sm font-bold text-gray-900">Type d'intervention</label>

                <div v-if="loadingInterventionTypes" class="py-2">
                    <i class="fas fa-spinner fa-spin"></i>
                </div>

                <div v-else class="relative mt-2">
                    <!-- Sélection de la catégorie logique (entretien, dépannage, etc.) -->
                    <div class="grid grid-cols-2 md:grid-cols-2 gap-3">
                        <div
                            v-for="group in groupedInterventionTypes"
                            :key="group.key"
                            @click="selectTypeIntervention(group.key)"
                            class="border rounded-lg p-4 cursor-pointer transition hover:shadow-md"
                            :class="filterTypeIntervention === group.key ? 'border-red-600 bg-red-50' : 'border-gray-300 hover:border-red-400'"
                        >
                            <div class="flex items-center gap-3">
                                <i class="fas fa-list text-gray-400"></i>
                                <span class="font-medium text-sm">{{ group.libelle }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Sélection du type précis (si plusieurs choix disponibles) -->
                    <div v-if="typesFiltered.length > 1" class="pt-4">
                        <label class="text-sm font-bold text-gray-900">Travaux à réaliser</label>
                        <div class="mt-2">
                            <!-- Type déjà sélectionné → affichage avec bouton modifier -->
                            <div v-if="dataModel.type_id" class="border-2 border-red-600 bg-red-50 rounded-lg p-4">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <i class="fas fa-wrench text-gray-400"></i>
                                        <span class="font-medium text-sm">{{ selectedTypeName }}</span>
                                    </div>
                                    <button
                                        @click="dataModel.type_id = null; $forceUpdate()"
                                        class="text-red-600 hover:text-red-700 text-sm font-medium"
                                    >
                                        <i class="fas fa-edit mr-1"></i> Modifier
                                    </button>
                                </div>
                            </div>

                            <!-- Grille de sélection du type -->
                            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div
                                    v-for="type in typesFiltered"
                                    :key="type.id"
                                    @click="selectDureeIntervention(type.id)"
                                    class="border rounded-lg p-4 cursor-pointer transition hover:shadow-md border-gray-300 hover:border-red-400"
                                >
                                    <div class="flex items-center gap-3">
                                        <i class="fas fa-wrench text-gray-400"></i>
                                        <span class="font-medium text-sm">{{ type.nom }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <!-- DUREE -->
            <div v-if="dataModel.type_id">

                <label class="text-sm font-bold text-gray-900">
                    Durée de l'intervention
                </label>

                <div class="mt-2 grid grid-cols-2 gap-3">

                    <select @change="updateDuree" v-model="dureeHeures" class="w-full rounded-lg border border-gray-300 text-sm py-2 px-2">

                        <option :value="0">0 h</option>

                        <option v-for="h in 12"
                                :key="h"
                                :value="h">

                            {{ h }} h

                        </option>

                    </select>

                    <select @change="updateDuree" v-model="dureeMinutes" class="w-full rounded-lg border border-gray-300 text-sm py-2 px-2">

                        <option v-for="m in minutesOptions"
                                :key="m"
                                :value="m">

                            {{ m }} min

                        </option>

                    </select>

                </div>

            </div>


            <!-- NOTES -->
            <div v-if="dataModel.type_id">

                <label class="text-sm font-bold text-gray-900">
                    Notes internes
                </label>

                <textarea v-model="dataModel.notes_internes"
                        rows="3"
                        placeholder="Notes techniques..."
                        class="w-full rounded-lg border border-gray-300 text-sm p-3">
                </textarea>

            </div>
        </div>
    </div>
</template>

<script>
import interventionTypeMappingConfig from './../../../config/InterventionTypeMapping.js';
import AppareilForm from '../../Client/Components/AppareilForm.vue'

export default {

    name: "InterventionFormSuite",

    components: { AppareilForm },

    props: {

        value: { type: Object, default: () => ({}) },

        client_id: { type: Number, required: true },

        adresse_id: { type: Number, required: true },

        travaux_id: { type: Number, default: null }

    },

    data() {

        return {
            dataModel: { ...this.value },

            appareils: [],
            types: [],

            newAppareil: {},

            filterTypeIntervention: '',
            interventionTypeMapping: interventionTypeMappingConfig,

            dureeHeures: 0,
            dureeMinutes: 0,
            minutesOptions: Array.from({ length: 60 }, (_, i) => i),

            loadingAppareil: false,
            loadingInterventionTypes: false,
            loadingAddAppareil: false,
            loadingCreateAppareil: false
        }

    },

    watch: {

        value: {
            handler(v) {

                const newValue = JSON.stringify(v)
                const oldValue = JSON.stringify(this.dataModel)

                if (newValue !== oldValue) {
                    this.dataModel = { ...v }
                }

            },
            deep: true
        },

        dataModel: {
            handler(v) {
                this.$forceUpdate()

                const newValue = JSON.stringify(v)
                const oldValue = JSON.stringify(this.value)

                if (newValue !== oldValue) {
                    this.$emit('input', { ...v })
                }

            },
            deep: true
        },

        dureeHeures() {
            this.updateDuree()
        },

        dureeMinutes() {
            this.updateDuree()
        }

    },

    computed: {

        groupedInterventionTypes() {

            const map = {
                premiere_visite: "Première visite",
                depannage: "Dépannage",
                entretien: "Entretien",
                installation: "Installation",
                diagnostic: "Diagnostic",
                mise_en_service: "Mise en service"
            }

            let grouped = []

            this.types.forEach(type => {

                if (map[type.type_logique] && !grouped.find(g => g.key === type.type_logique)) {

                    grouped.push({
                        key: type.type_logique,
                        libelle: map[type.type_logique]
                    })

                }

            })

            return grouped

        },

        /** Types d'intervention correspondant à la catégorie active */
        typesFiltered() {
            if (!this.filterTypeIntervention) return []
            return this.types.filter(t => t.type_logique === this.filterTypeIntervention)
        },

        sousTypesFiltered() {
            if (!this.filterTypeIntervention) {
                return []
            }

            let values = this.types.filter(t => t.type_logique === this.filterTypeIntervention)

            if (values.length === 1) {
                this.selectDureeIntervention(values[0].id)
            }

            return values
        }

    },

    methods: {

        async loadAppareils() {

            this.loadingAppareil = true

            try {

                const res = await this.$request.get(`/client-appareils?client_id=${this.client_id}&all=1`)

                this.appareils = res

            } catch (e) {

                console.error(e)

            }

            this.loadingAppareil = false

        },

        async loadTypesIntervention() {

            const appareil = this.appareils.find(a => a.id === this.dataModel.client_appareil_id)
            if (!appareil) return

            const categorie = this.interventionTypeMapping[appareil.type] || appareil.type

            this.loadingInterventionTypes = true

            this.types = await this.$request.post('/intervention-types/list', {
                categorie: categorie
            })

            this.loadingInterventionTypes = false

        },

        /** Bascule sur une catégorie logique et sélectionne automatiquement si unique */
        selectTypeIntervention(typeKey) {
            this.filterTypeIntervention = typeKey
            this.dataModel.type_id      = null
            this.$forceUpdate()

            this.$nextTick(() => {
                if (this.typesFiltered.length === 1) {
                    this.selectDureeIntervention(this.typesFiltered[0].id)
                }
            })
        },

        selectDureeIntervention(id) {

            this.dataModel.type_id = id

            const selected = this.types.find(t => t.id === id)

            if (selected && selected.time_min) {

                const minutes = Number(selected.time_min)

                this.dureeHeures = Math.floor(minutes / 60)

                this.dureeMinutes = minutes % 60

                this.updateDuree()

            }

        },

        updateDuree() {
            this.dataModel.duree_estimee = (this.dureeHeures * 60) + this.dureeMinutes
            this.$emit('input', { ...this.dataModel })
        },

        /** Valide et enregistre un nouvel appareil pour le client courant */
        async saveAppareil() {
            const valid = this.$inpError(['inp-type:required|min=3'])
            if (!valid) { this.$toast.error('Formulaire invalide'); return }

            this.loadingAddAppareil = this.loadingCreateAppareil = true
            try {
                this.newAppareil.client_id = this.dataModel.client_id
                const response = await this.$request.post('/client-appareils', this.newAppareil)
                this.$modal.hide('modal-add-appareil-intervention')
                this.newAppareil = {}
                await this.loadAppareils() // Recharger la liste des appareils
                
                this.dataModel.client_appareil_id = response.appareil.id
                await this.loadTypesIntervention()
                this.$forceUpdate()
            } catch {
                this.$toast.error("Erreur lors de l'enregistrement de l'appareil")
            } finally {
                this.loadingAddAppareil = this.loadingCreateAppareil = false
            }
        },

    },

    async mounted() {

        this.dataModel.client_id = this.client_id
        this.dataModel.adresse_id = this.adresse_id
        this.dataModel.travaux_id = this.travaux_id

        await this.loadAppareils()

    }

}

</script>